import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  FullScreenLoading,
} from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import type { Client } from 'persona';
import type React from 'react';
import type { Dispatch } from 'react';
import { skipVerification } from '~/api/user2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import rootStore from '../../store';

type Props = {
  personaClient: () => Client;
  isPersonaLoading: boolean;
  setPersonaLoading: Dispatch<React.SetStateAction<boolean>>;
};

export default function ConfirmSkipVerification({ personaClient, isPersonaLoading, setPersonaLoading }: Props) {
  const navigate = useNavigate();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="verification-skip-trigger">
          Skip Verification
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Are you sure?"
        className="z-[110] w-auto min-w-min max-w-[75vw] max-w-xl"
        overlayClassName="z-[100]"
        data-testid="verification-skip-dialog"
      >
        {isPersonaLoading && <FullScreenLoading />}
        <DialogBody className="text-sm">
          Unverified users will not be able to view certain critical data layers and verification is recommended for
          optimal platform experience. It only takes three minutes to complete.
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            data-testid="verification-skip-confirm"
            onClick={async () => {
              rootStore.setinUnverifiedMode(true);
              navigate({ to: '/home' });
              capturePosthogEvent(POSTHOG_EVENTS.verificationSkipped);
              await skipVerification();
            }}
          >
            Skip Verification
          </Button>
          <Button
            data-testid="verification-verify-now"
            onClick={() => {
              setPersonaLoading(true);
              personaClient();
              capturePosthogEvent(POSTHOG_EVENTS.verificationStarted);
            }}
          >
            Verify Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
